import React, { Component, useEffect } from 'react';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import { trackEvent } from 'src/services/analytics';
import styles from './InstitutionVideo.module.scss';

interface Props {
	programId: string;
	institution: GetMemberProgramQuery['memberProgram']['studentFacingAcademicPartner'];
}

const BaseInstitutionVideo: React.FC<Props> = ({ institution, programId }) => {
	useEffect(() => {
		if (institution.videoId && window.YT) {
			const player = new window.YT.Player('institution-video', {
				events: {
					onStateChange: e => {
						if (e.data !== window.YT.PlayerState.PLAYING) {
							return;
						}

						trackEvent('Video Played', {
							programId,
							institutionID: institution.uuid,
							video: player.getIframe().getAttribute('title'),
							id: institution.videoId,
						});
					},
				},
			});
		}
	}, [institution, programId]);

	if (!window.YT) {
		return null;
	}

	if (!institution.videoId) {
		return null;
	}

	return (
		<div className={styles.videoContainer}>
			<iframe
				allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
				allowFullScreen
				className={styles.video}
				id='institution-video'
				src={`https://www.youtube.com/embed/${institution.videoId}?enablejsapi=1&origin=${window.location.origin}`}
				title='Institution video'
			></iframe>
		</div>
	);
};

class VideoErrorBoundary extends Component {
	public readonly state: { hasError: boolean; error: unknown; info: unknown };
	constructor(props) {
		super(props);
		this.state = { hasError: false, error: null, info: null };
	}

	public static getDerivedStateFromError(error: unknown) {
		return { hasError: true, error };
	}

	public componentDidCatch(error: unknown, info: unknown) {
		this.setState({ error, info });
	}

	public render() {
		if (this.state.hasError) {
			return null;
		}
		return this.props.children;
	}
}

export const InstitutionVideo: React.FC<Props> = ({ institution, programId }) => {
	return (
		<VideoErrorBoundary>
			<BaseInstitutionVideo institution={institution} programId={programId} />
		</VideoErrorBoundary>
	);
};
